import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, ComposedChart, Label,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  TOTAL_ROW, OTHER_ROW, cellText, isNumericColumn, isCategoryColumn, dataRows, tableFromRows, withTotalRow,
  withComparisonChange, compactTable, pivotTable, chartModel, colorFor, scaleRanges, IDENTIFIER_LABEL,
} from '../../utils/reportTable';
import { formatMeasure, formatChange, formatTick } from '../../utils/valueFormat';

/**
 * Renders a report's results table as a table, pivot, chart or stat. The
 * dashboard widget, the report page and the editor's preview all use it, so
 * what is previewed is what is shown.
 *
 * Props:
 *   table       the results table (see utils/reportTable), or
 *   rows        plain rows keyed by label, for simple callers
 *   config      { displayType, target, colorRules, pivot, totalRow, compact, compactRows, formats,
 *                 orientation ('horizontal' for sideways bars), stacking ('stacked', 'percent', 'none'),
 *                 lineSeries (values a combined chart draws as lines), pieStyle ('pie' for a full pie), pieSlices }
 *   onSelect    (row, event, { measure }) when a row, bar, slice or stat is
 *               clicked; measure is the figure clicked, when known
 *   selected    (row) => bool, to highlight the selected category
 */

const PALETTE = ['#2889C6', '#27ae60', '#e67e22', '#8e44ad', '#f39c12', '#16a085', '#e74c3c', '#3498db', '#9b59b6', '#1abc9c'];
const TOOLTIP_STYLE = { backgroundColor: 'var(--widget-bg)', border: '1px solid var(--widget-border)', borderRadius: '4px', color: 'var(--widget-text)' };
const TOOLTIP_LABEL = { color: 'var(--widget-text)' };
const AXIS = { fontSize: '0.72rem' };
const GRID = 'rgba(255,255,255,0.08)';
const AXIS_STROKE = 'rgba(255,255,255,0.55)';

const NoData = () => <div className="report-status">No data</div>;

/** The format shared by all the series, for the axis; undefined when they differ. */
const sharedFormat = (series) => {
  const found = new Set(series.map((s) => s.format || null));
  return found.size === 1 ? [...found][0] : undefined;
};
const tooltipFormatter = (series) => (value, name, item) => formatMeasure(value, series.find((s) => s.key === item?.dataKey)?.format);

function TargetLine({ target, format }) {
  if (target === undefined || target === null || target === '') return null;
  return <ReferenceLine y={Number(target)} stroke="#ffc107" strokeDasharray="5 4" label={{ value: `Target ${formatMeasure(Number(target), format)}`, fill: '#ffc107', fontSize: 11, position: 'insideTopRight' }} />;
}

// ---------------------------------------------------------------------------
// Table and pivot
// ---------------------------------------------------------------------------

// Text up to this length stays on one line; longer text wraps.
const ONE_LINE_CHARS = 24;

function columnLayout(column, rows) {
  if (isNumericColumn(column)) return { className: 'report-td--numeric report-td--fit' };
  const longest = rows.reduce((max, r) => Math.max(max, cellText(column, r[column.key]).length), 0);
  if (longest <= ONE_LINE_CHARS) return { className: 'report-td--fit' };
  return { style: { minWidth: `${Math.min(32, Math.max(16, Math.ceil(longest * 0.6)))}ch` } };
}

function TableView({ table, config, onSelect, selected }) {
  const rows = table.rows;
  if (dataRows(table).length === 0) return <NoData />;
  const { table: shown, hidden } = config.compact ? compactTable(table, config.compactRows || 5) : { table, hidden: 0 };
  const rules = config.colorRules || [];
  const ranges = scaleRanges(table, rules);
  const layout = Object.fromEntries(table.columns.map((c) => [c.key, columnLayout(c, rows)]));
  const clickable = typeof onSelect === 'function';
  const identifier = (c) => !isNumericColumn(c) && IDENTIFIER_LABEL.test(c.label);

  return (
    <div className={`report-table-wrapper ${config.compact ? 'report-table-wrapper--compact' : ''}`}>
      <table className="widget-table report-table">
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c.key} className={isNumericColumn(c) ? 'report-th--numeric' : undefined}
                title={c.role === 'comparison' || c.role === 'change' ? undefined : c.error || undefined}>
                {c.label}{c.error ? ' ⚠' : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.rows.map((row, i) => {
            const isTotal = Boolean(row[TOTAL_ROW]);
            const canClick = clickable && !isTotal;
            const isSelected = selected ? selected(row) : false;
            return (
              <tr key={i}
                className={[isTotal && 'report-tr--total', row[OTHER_ROW] && 'report-tr--other', canClick && 'report-tr--clickable', isSelected && 'report-tr--selected'].filter(Boolean).join(' ') || undefined}
                onClick={canClick ? (e) => onSelect(row, e) : undefined}
                tabIndex={canClick ? 0 : undefined}
                onKeyDown={canClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row, e); } } : undefined}>
                {table.columns.map((c) => {
                  const v = row[c.key];
                  const color = isTotal ? null : colorFor(rules, c, v, ranges);
                  const text = identifier(c) ? (v === null || v === undefined || v === '' ? '—' : String(v)) : c.role === 'change' && typeof v === 'number' ? `${v > 0 ? '+' : ''}${formatMeasure(v, 'percent')}` : cellText(c, v);
                  return (
                    <td key={c.key} className={[layout[c.key].className, c.role === 'comparison' && 'report-td--comparison', c.role === 'change' && v !== null && (v > 0 ? 'report-td--up' : v < 0 ? 'report-td--down' : '')].filter(Boolean).join(' ') || undefined}
                      style={{ ...layout[c.key].style, ...(color ? { '--cell-color': color } : {}) }}
                      data-colored={color ? 'true' : undefined}>
                      {text}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {hidden > 0 && <div className="report-status report-more">+{hidden} more row{hidden === 1 ? '' : 's'}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat
// ---------------------------------------------------------------------------

function StatView({ table, config, onSelect }) {
  const rows = dataRows(table);
  const column = table.columns.find((c) => c.role === 'measure' || c.role === 'calculation');
  if (!column || rows.length === 0) return <NoData />;
  const value = rows[0][column.key];
  const label = config.statLabel || column.label;
  const previous = table.comparison?.totals?.[column.key] ?? rows[0][`compare:${column.key}`];
  let delta = null;
  if (typeof value === 'number' && typeof previous === 'number') {
    const diff = value - previous;
    delta = { diff, pct: previous === 0 ? null : diff / Math.abs(previous), up: diff > 0, flat: diff === 0 };
  }
  const target = config.target !== undefined && config.target !== null && config.target !== '' ? Number(config.target) : null;
  const ruleColor = colorFor(config.colorRules || [], column, value, scaleRanges(table, config.colorRules || []));
  const onTarget = target !== null && typeof value === 'number' ? value >= target : null;
  const versus = table.comparison?.kind === 'lastYear' ? 'vs same period last year' : 'vs previous period';

  return (
    <div className="report-stat">
      <div className="report-stat-label">{label}</div>
      {/* The figure itself opens the rows behind it, when the widget allows. */}
      <div className={`report-stat-value ${!ruleColor && onTarget !== null ? (onTarget ? 'report-stat-value--good' : 'report-stat-value--bad') : ''} ${onSelect ? 'report-stat-value--clickable' : ''}`}
        style={ruleColor ? { color: ruleColor } : undefined}
        {...(onSelect ? {
          role: 'button', tabIndex: 0, title: 'Show the rows behind this',
          onClick: (e) => onSelect(rows[0], e, { measure: column.role === 'measure' ? column.key : null }),
          onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(rows[0], e, { measure: column.role === 'measure' ? column.key : null }); } },
        } : {})}>
        {cellText(column, value)}
      </div>
      {delta && (
        <div className={`report-stat-delta ${delta.flat ? '' : delta.up ? 'up' : 'down'}`}>
          {delta.flat ? '→' : delta.up ? '↑' : '↓'} {formatChange(delta.diff, column.format)}
          {delta.pct !== null && column.format !== 'percent' && ` (${Math.abs(delta.pct * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%)`} {versus}
        </div>
      )}
      {target !== null && <div className="report-stat-target">Target {formatMeasure(target, column.format)}</div>}
      {rows.length > 1 && <div className="report-stat-hint">The query returned {rows.length} rows; the stat shows the first.</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

/** The row a chart point stands for, for a click on a series. */
const rowOf = (point, seriesKey, split) => (split ? point?.__rows?.[seriesKey] : point?.__row) || null;

// Category labels on a sideways bar chart: wide enough for most names, cut short past this.
const SIDE_LABEL_CHARS = 28;
const shorten = (text, max = SIDE_LABEL_CHARS) => (String(text).length > max ? `${String(text).slice(0, max - 1)}…` : String(text));
const percentTick = (v) => `${Math.round(v * 100)}%`;

/**
 * How bars or areas stack: 'stacked' (on top of each other), 'percent' (each
 * category's total as 100%), 'none', or automatically (a breakdown of more
 * than three series is stacked).
 */
function stackingOf(config, model) {
  const s = config.stacking;
  if (s === 'stacked' || s === 'percent' || s === 'none') return s;
  return model.split && model.series.length > 3 ? 'stacked' : 'none';
}

function BarView({ table, config, onSelect, selected }) {
  const model = chartModel(table);
  if (!model || model.data.length === 0) return dataRows(table).length ? <div className="report-status report-status--error">A bar chart needs a category and a value.</div> : <NoData />;
  const format = sharedFormat(model.series);
  const rules = config.colorRules || [];
  const ranges = scaleRanges(table, rules);
  const stacking = stackingOf(config, model);
  const sideways = config.orientation === 'horizontal';
  const percent = stacking === 'percent';
  const anySelected = selected && model.data.some((p) => (model.split ? Object.values(p.__rows) : [p.__row]).some((r) => r && selected(r)));
  const click = (seriesKey) => (onSelect ? (point, index, event) => {
    const row = rowOf(point?.payload || point, seriesKey, model.split);
    const s = model.series.find((x) => x.key === seriesKey);
    if (row) onSelect(row, event, { measure: s?.of || s?.source || null });
  } : undefined);
  const valueTick = (v) => (percent ? percentTick(v) : formatTick(v, format));
  const labelWidth = Math.min(190, Math.max(56, Math.max(...model.data.map((p) => Math.min(SIDE_LABEL_CHARS, String(p.__x).length))) * 6.4 + 12));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={model.data} layout={sideways ? 'vertical' : 'horizontal'} stackOffset={percent ? 'expand' : undefined}
        margin={sideways ? { top: 8, right: 20, left: 4, bottom: 8 } : { top: 8, right: 16, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={!sideways} vertical={sideways} />
        {sideways ? (
          <>
            <XAxis type="number" stroke={AXIS_STROKE} style={AXIS} tickFormatter={valueTick} />
            <YAxis type="category" dataKey="__x" stroke={AXIS_STROKE} style={AXIS} width={labelWidth} interval={0} tickFormatter={(v) => shorten(v)} />
          </>
        ) : (
          <>
            <XAxis dataKey="__x" stroke={AXIS_STROKE} style={AXIS} interval={model.split ? 'preserveStartEnd' : 0} angle={-20} textAnchor="end" height={54} />
            <YAxis stroke={AXIS_STROKE} style={AXIS} tickFormatter={valueTick} />
          </>
        )}
        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} formatter={tooltipFormatter(model.series)} cursor={{ fill: 'rgba(255,255,255,0.06)' }} />
        {model.series.length > 1 && <Legend wrapperStyle={{ fontSize: '0.75rem' }} />}
        {model.series.map((s, i) => {
          const base = PALETTE[(model.split ? i : s.colorIndex) % PALETTE.length];
          const column = table.columns.find((c) => c.key === s.source);
          // A comparison stacks with the other comparisons, beside the period's own stack.
          const stackId = stacking === 'none' ? undefined : s.comparison ? 'compare' : 'series';
          return (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={base} fillOpacity={s.comparison ? 0.35 : 1}
              stackId={stackId} isAnimationActive={false} onClick={click(s.key)} radius={stackId ? 0 : sideways ? [0, 3, 3, 0] : [3, 3, 0, 0]}
              cursor={onSelect ? 'pointer' : undefined}>
              {!model.split && model.data.map((p, j) => {
                const color = s.comparison ? null : colorFor(rules, column, p[s.key], ranges);
                const dim = anySelected && p.__row && !selected(p.__row);
                return <Cell key={j} fill={color || base} fillOpacity={(s.comparison ? 0.35 : 1) * (dim ? 0.3 : 1)} />;
              })}
            </Bar>
          );
        })}
        {!percent && (sideways
          ? (config.target !== undefined && config.target !== null && config.target !== '' && <ReferenceLine x={Number(config.target)} stroke="#ffc107" strokeDasharray="5 4" label={{ value: `Target ${formatMeasure(Number(config.target), format)}`, fill: '#ffc107', fontSize: 11, position: 'insideTopRight' }} />)
          : <TargetLine target={config.target} format={format} />)}
      </BarChart>
    </ResponsiveContainer>
  );
}

function LineView({ table, config, onSelect }) {
  const model = chartModel(table);
  if (!model || model.data.length === 0) return dataRows(table).length ? <div className="report-status report-status--error">A line chart needs a date or category and a value.</div> : <NoData />;
  const format = sharedFormat(model.series);
  const area = config.displayType === 'area';
  const Chart = area ? AreaChart : LineChart;
  const Series = area ? Area : Line;
  // Areas stack when asked to, or by default when they break a value down.
  const stacking = area ? (['stacked', 'percent', 'none'].includes(config.stacking) ? config.stacking : model.split ? 'stacked' : 'none') : 'none';
  const percent = stacking === 'percent';
  const click = onSelect ? (state, event) => {
    const point = state?.activePayload?.[0]?.payload;
    const row = model.split ? Object.values(point?.__rows || {})[0] : point?.__row;
    if (row && !model.split) onSelect(row, event, { measure: model.series[0]?.of || null });
  } : undefined;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Chart data={model.data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} onClick={click} stackOffset={percent ? 'expand' : undefined}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
        <XAxis dataKey="__x" stroke={AXIS_STROKE} style={AXIS} />
        <YAxis stroke={AXIS_STROKE} style={AXIS} tickFormatter={(v) => (percent ? percentTick(v) : formatTick(v, format))} />
        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} formatter={tooltipFormatter(model.series)} />
        {model.series.length > 1 && <Legend wrapperStyle={{ fontSize: '0.75rem' }} />}
        {model.series.map((s, i) => {
          const color = PALETTE[(model.split ? i : s.colorIndex) % PALETTE.length];
          return (
            <Series key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={color} fill={color}
              fillOpacity={area ? (s.comparison ? 0.1 : 0.25) : 1} strokeOpacity={s.comparison ? 0.55 : 1}
              strokeDasharray={s.comparison ? '5 4' : undefined} stackId={stacking !== 'none' && !s.comparison ? 'series' : undefined}
              strokeWidth={2} isAnimationActive={false} dot={model.data.length < 40 && !s.comparison} connectNulls />
          );
        })}
        {!percent && <TargetLine target={config.target} format={format} />}
      </Chart>
    </ResponsiveContainer>
  );
}

/** The values a combined chart draws as lines: as chosen, or every value after the first. */
export function comboLines(config, values) {
  if (Array.isArray(config.lineSeries)) return new Set(config.lineSeries);
  return new Set(values.slice(1));
}

/**
 * Bars and lines on one chart, such as turnover as bars with gross margin %
 * as a line. Lines whose values are a different kind from the bars (a
 * percentage against money, say) get their own axis on the right.
 */
function ComboView({ table, config, onSelect, selected }) {
  const model = chartModel(table);
  if (!model || model.data.length === 0) return dataRows(table).length ? <div className="report-status report-status--error">A bar and line chart needs a category and values.</div> : <NoData />;
  if (model.split) return <div className="report-status report-status--error">A bar and line chart shows values against one category. Group by one field, and add the values to compare.</div>;
  const values = [...new Set(model.series.map((s) => s.of))];
  const lines = comboLines(config, values);
  const kind = (s) => (lines.has(s.of) ? 'line' : 'bar');
  const bars = model.series.filter((s) => kind(s) === 'bar');
  const lineSeries = model.series.filter((s) => kind(s) === 'line');
  const leftFormat = bars.length ? sharedFormat(bars) : sharedFormat(lineSeries);
  // A line goes on the right when the bars are one kind of value and the lines another.
  const rightAxis = bars.length > 0 && lineSeries.length > 0 && lineSeries.some((s) => (s.format || null) !== (leftFormat ?? null));
  const rightFormat = rightAxis ? sharedFormat(lineSeries) : undefined;
  const anySelected = selected && model.data.some((p) => p.__row && selected(p.__row));
  const click = (seriesKey) => (onSelect ? (point, index, event) => {
    const row = rowOf(point?.payload || point, seriesKey, false);
    const s = model.series.find((x) => x.key === seriesKey);
    if (row) onSelect(row, event, { measure: s?.of || null });
  } : undefined);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={model.data} margin={{ top: 8, right: rightAxis ? 4 : 16, left: 0, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey="__x" stroke={AXIS_STROKE} style={AXIS} interval="preserveStartEnd" angle={-20} textAnchor="end" height={54} />
        <YAxis yAxisId="left" stroke={AXIS_STROKE} style={AXIS} tickFormatter={(v) => formatTick(v, leftFormat)} />
        {rightAxis && <YAxis yAxisId="right" orientation="right" stroke={AXIS_STROKE} style={AXIS} tickFormatter={(v) => formatTick(v, rightFormat)} />}
        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} formatter={tooltipFormatter(model.series)} cursor={{ fill: 'rgba(255,255,255,0.06)' }} />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        {bars.map((s) => {
          const color = PALETTE[s.colorIndex % PALETTE.length];
          return (
            <Bar key={s.key} yAxisId="left" dataKey={s.key} name={s.label} fill={color} fillOpacity={s.comparison ? 0.35 : 0.9}
              radius={[3, 3, 0, 0]} isAnimationActive={false} onClick={click(s.key)} cursor={onSelect ? 'pointer' : undefined}>
              {model.data.map((p, j) => <Cell key={j} fillOpacity={(s.comparison ? 0.35 : 0.9) * (anySelected && p.__row && !selected(p.__row) ? 0.3 : 1)} />)}
            </Bar>
          );
        })}
        {lineSeries.map((s) => {
          const color = PALETTE[s.colorIndex % PALETTE.length];
          return (
            <Line key={s.key} yAxisId={rightAxis ? 'right' : 'left'} type="monotone" dataKey={s.key} name={s.label} stroke={color}
              strokeWidth={2.5} strokeOpacity={s.comparison ? 0.55 : 1} strokeDasharray={s.comparison ? '5 4' : undefined}
              dot={model.data.length < 40 ? { r: 3, fill: color } : false} isAnimationActive={false} connectNulls />
          );
        })}
        {config.target !== undefined && config.target !== null && config.target !== '' && (
          <ReferenceLine yAxisId="left" y={Number(config.target)} stroke="#ffc107" strokeDasharray="5 4" label={{ value: `Target ${formatMeasure(Number(config.target), leftFormat)}`, fill: '#ffc107', fontSize: 11, position: 'insideTopRight' }} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// A pie keeps this many slices, the rest added together as Other, unless the report says otherwise.
export const DEFAULT_PIE_SLICES = 8;
// Slices smaller than this share of the whole carry no percentage, which would not fit.
const LABEL_MIN_SHARE = 0.05;

/** The slices of a pie: largest first, with the smallest added together as Other beyond the limit. */
export function pieSlices(rows, category, value, limit = DEFAULT_PIE_SLICES) {
  const all = rows
    .map((r) => ({ name: cellText(category, r[category.key]), value: r[value.key], row: r }))
    .filter((d) => typeof d.value === 'number' && d.value > 0)
    .sort((a, b) => b.value - a.value);
  const keep = Math.max(2, Number(limit) || DEFAULT_PIE_SLICES);
  if (all.length <= keep) return all;
  const rest = all.slice(keep - 1);
  return [...all.slice(0, keep - 1), { name: 'Other', value: rest.reduce((t, d) => t + d.value, 0), row: null, other: rest.length }];
}

function SliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < LABEL_MIN_SHARE) return null;
  const r = innerRadius + (outerRadius - innerRadius) * (innerRadius > 0 ? 0.5 : 0.62);
  const x = cx + r * Math.cos((-midAngle * Math.PI) / 180);
  const y = cy + r * Math.sin((-midAngle * Math.PI) / 180);
  return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" className="report-pie-label">{`${Math.round(percent * 100)}%`}</text>;
}

/**
 * A pie, as a ring with the total in the middle unless pieStyle is 'pie'.
 * Slices carry their percentage, the smallest are added together as Other,
 * and the legend beside it lists each slice's share.
 */
function PieView({ table, config, onSelect, selected }) {
  const category = table.columns.find(isCategoryColumn);
  const value = table.columns.find((c) => c.role === 'measure' || c.role === 'calculation');
  if (!category || !value) return dataRows(table).length ? <div className="report-status report-status--error">A pie chart needs a category and a value.</div> : <NoData />;
  const data = pieSlices(dataRows(table), category, value, config.pieSlices);
  if (data.length === 0) return <NoData />;
  const total = data.reduce((t, d) => t + d.value, 0);
  const ring = config.pieStyle !== 'pie';
  const anySelected = selected && data.some((d) => d.row && selected(d.row));
  const pick = (d, event) => { if (onSelect && d.row) onSelect(d.row, event, { measure: value.key }); };
  const colorOf = (d, i) => (d.row ? PALETTE[i % PALETTE.length] : '#7f8c8d');

  return (
    <div className="report-pie">
      <div className="report-pie-chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={ring ? '58%' : 0} outerRadius="88%" paddingAngle={ring ? 2 : 1}
              stroke="var(--widget-bg)" strokeWidth={ring ? 2 : 1} labelLine={false} label={SliceLabel} isAnimationActive={false}
              onClick={onSelect ? (d, i, e) => pick(data[i], e) : undefined} cursor={onSelect ? 'pointer' : undefined}>
              {data.map((d, i) => <Cell key={i} fill={colorOf(d, i)} fillOpacity={anySelected && !(d.row && selected(d.row)) ? 0.3 : 1} />)}
              {ring && (
                <Label position="center" content={({ viewBox }) => (
                  <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="central" className="report-pie-total">
                    <tspan x={viewBox.cx} dy="-0.5em" className="report-pie-total-value">{formatMeasure(total, value.format)}</tspan>
                    <tspan x={viewBox.cx} dy="1.5em" className="report-pie-total-label">Total</tspan>
                  </text>
                )} />
              )}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL}
              formatter={(v, name) => [`${formatMeasure(v, value.format)} (${Math.round((v / total) * 1000) / 10}%)`, name]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="report-pie-legend" aria-label={`${value.label} by ${category.label}`}>
        {data.map((d, i) => (
          <li key={d.name}>
            <button type="button" className={`report-pie-key ${anySelected && !(d.row && selected(d.row)) ? 'is-dim' : ''}`} disabled={!onSelect || !d.row}
              onClick={(e) => pick(d, e)} title={`${d.name}: ${formatMeasure(d.value, value.format)}${d.other ? ` (${d.other} others)` : ''}`}>
              <span className="report-pie-swatch" style={{ background: colorOf(d, i) }} aria-hidden="true" />
              <span className="report-pie-name">{d.name}</span>
              <span className="report-pie-share">{`${Math.round((d.value / total) * 100)}%`}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

const ReportDisplay = ({ table, rows, config = {}, onSelect, selected }) => {
  const source = useMemo(() => table || tableFromRows(rows || [], config.formats || {}), [table, rows, config.formats]);
  const displayType = config.displayType || 'table';

  const shaped = useMemo(() => {
    if (displayType === 'pivot' && config.pivot) return pivotTable(source, config.pivot);
    if (displayType === 'table') return withComparisonChange(config.totalRow ? withTotalRow(source) : source);
    return source;
  }, [source, displayType, config.pivot, config.totalRow]);

  switch (displayType) {
    case 'stat': return <StatView table={shaped} config={config} onSelect={onSelect} />;
    case 'bar': return <BarView table={shaped} config={config} onSelect={onSelect} selected={selected} />;
    case 'line':
    case 'area': return <LineView table={shaped} config={config} onSelect={onSelect} />;
    case 'combo': return <ComboView table={shaped} config={config} onSelect={onSelect} selected={selected} />;
    case 'pie': return <PieView table={shaped} config={config} onSelect={onSelect} selected={selected} />;
    default: return <TableView table={shaped} config={config} onSelect={shaped.pivot ? (onSelect && ((r, e) => onSelect(r.__raw ? { ...r.__raw, [OTHER_ROW]: r[OTHER_ROW] } : r, e))) : onSelect} selected={selected && shaped.pivot ? (r) => selected(r.__raw || {}) : selected} />;
  }
};

export default ReportDisplay;
